import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

/**
 * Route-level code splitting via dynamic import: the admin screens are a large
 * chunk that a customer never downloads.
 *
 * `meta.role` drives the single guard below. RBAC is enforced server side; this
 * only keeps the UI honest.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/pages/HomePage.vue'),
    meta: { title: 'Home' },
  },
  {
    path: '/katalog',
    name: 'catalog',
    component: () => import('@/pages/CatalogPage.vue'),
    meta: { title: 'Catalogue' },
  },
  {
    path: '/buku/:id',
    name: 'book-detail',
    component: () => import('@/pages/BookDetailPage.vue'),
    meta: { title: 'Book' },
  },
  {
    path: '/artikel',
    name: 'articles',
    component: () => import('@/pages/ArticlesPage.vue'),
    meta: { title: 'Articles' },
  },
  {
    path: '/artikel/:slug',
    name: 'article-detail',
    component: () => import('@/pages/ArticleDetailPage.vue'),
    meta: { title: 'Article' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { title: 'Sign in', guestOnly: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/RegisterPage.vue'),
    meta: { title: 'Create account', guestOnly: true },
  },
  {
    path: '/lupa-kata-sandi',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { title: 'Lupa Kata Sandi', guestOnly: true },
  },
  {
    path: '/reset-kata-sandi',
    name: 'reset-password',
    component: () => import('@/pages/ResetPasswordPage.vue'),
    meta: { title: 'Atur Ulang Kata Sandi', guestOnly: true },
  },
  {
    // Reached from the profile icon in the navbar. Holds the customer's details
    // and their rentals, so there is one place a customer goes for "my stuff".
    path: '/profil',
    name: 'profile',
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { title: 'My profile', role: 'CUSTOMER' },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/pages/admin/AdminDashboardPage.vue'),
    meta: { title: 'Admin', role: 'ADMIN' },
  },
  {
    path: '/admin/dashboard',
    name: 'admin-analytics',
    component: () => import('@/pages/admin/AdminAnalyticsDashboardPage.vue'),
    meta: { title: 'Dashboard', role: 'ADMIN' },
  },
  {
    path: '/admin/katalog',
    name: 'admin-books',
    component: () => import('@/pages/admin/AdminBooksPage.vue'),
    meta: { title: 'Manage catalogue', role: 'ADMIN' },
  },
  {
    path: '/admin/transaksi',
    name: 'admin-rentals',
    component: () => import('@/pages/admin/AdminRentalsPage.vue'),
    meta: { title: 'Transactions', role: 'ADMIN' },
  },
  {
    path: '/admin/pelanggan',
    name: 'admin-customers',
    component: () => import('@/pages/admin/AdminCustomersPage.vue'),
    meta: { title: 'Customers', role: 'ADMIN' },
  },
  {
    path: '/admin/master-data',
    name: 'admin-master',
    component: () => import('@/pages/admin/AdminMasterDataPage.vue'),
    meta: { title: 'Master Data', role: 'ADMIN' },
  },
  {
    path: '/admin/artikel',
    name: 'admin-articles',
    component: () => import('@/pages/admin/AdminArticlesPage.vue'),
    meta: { title: 'Manage articles', role: 'ADMIN' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { title: 'Page not found' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (to, _from, savedPosition) => {
    if (savedPosition) return savedPosition;
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ el: to.hash, top: 80, behavior: 'smooth' });
        }, 250);
      });
    }
    return { top: 0 };
  },
});

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'Noorway Book Rent';

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // A deep link on a cold load must wait for the refresh attempt, otherwise a
  // signed-in user gets bounced to /login for a frame.
  if (auth.isBootstrapping) {
    await auth.bootstrap();
  }

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: auth.isAdmin ? 'admin' : 'profile' };
  }

  const requiredRole = to.meta.role as 'ADMIN' | 'CUSTOMER' | undefined;
  if (requiredRole) {
    if (!auth.isAuthenticated) {
      // Remember where they were headed so login can return them there.
      return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (auth.user?.role !== requiredRole) {
      return { name: auth.isAdmin ? 'admin' : 'profile' };
    }
  }

  return true;
});

router.afterEach((to) => {
  const title = to.meta.title as string | undefined;
  document.title = title ? `${title} · ${APP_NAME}` : APP_NAME;
});
